import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle, Copy, Trash2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { invitationAPI, profileAPI } from '../api/index';
import { Button } from '../components/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PageHeader } from '../components/PageHeader';
import { useAnalytics } from '../hooks/useAnalytics';
import { formatDate, getInitials, timeAgo } from '../lib/utils';
import { validateEmail, validatePhone } from '../lib/validation';
import { useAuthStore } from '../store/auth';

export function TeamPage() {
  const user = useAuthStore((s) => s.user)
  const { trackButtonClick } = useAnalytics()
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [invitePhone, setInvitePhone] = useState('')
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  // Fetch agents
  const { data: profilesRes, refetch: refetchProfiles, isLoading: profilesLoading } = useQuery({
    queryKey: ['profiles'],
    queryFn: () => profileAPI.list().then((r) => r.data),
    enabled: user?.role === 'admin',
  })

  const profiles = profilesRes?.profiles || []

  // Fetch invitations
  const { data: invitationsRes, refetch: refetchInvitations, isLoading: invitationsLoading } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => invitationAPI.list().then((r) => r.data),
    enabled: user?.role === 'admin',
  })

  const invitations = invitationsRes?.invitations || []

  // Mutations
  const inviteMutation = useMutation({
    mutationFn: () => invitationAPI.create({ email: inviteEmail, phone: invitePhone }),
    onSuccess: (res) => {
      toast.success('Invitation sent!')
      setInviteLink(res.data.invite_link || '')
      setInviteEmail('')
      setInvitePhone('')
      refetchInvitations()
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to send invitation')
    },
  })

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      profileAPI.updateStatus(id, is_active),
    onSuccess: () => {
      toast.success('Agent status updated')
      refetchProfiles()
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to update status')
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => invitationAPI.revoke(id),
    onSuccess: () => {
      toast.success('Invitation revoked')
      setConfirmDelete(null)
      refetchInvitations()
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to revoke invitation')
    },
  })

  // Admin check
  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-gray-600">You don't have permission to view this page.</p>
      </div>
    )
  }

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteEmail) {
      toast.error('Email is required')
      return
    }

    // Validate email
    if (!validateEmail(inviteEmail)) {
      toast.error('Please enter a valid email address')
      return
    }

    // Validate phone if provided
    if (invitePhone && !validatePhone(invitePhone)) {
      toast.error('Please enter a valid phone number (10 digits)')
      return
    }

    inviteMutation.mutate()
  }

  const handleCopyLink = () => {
    trackButtonClick('Copy Invite Link Modal', { page: 'team' })
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink)
      toast.success('Invite link copied!')
    }
  }

  const handleShareWhatsApp = () => {
    trackButtonClick('Share Invite on WhatsApp', { page: 'team' })
    if (inviteLink) {
      const msg = `You have been invited to join RTO Agents.\nClick here to accept: ${inviteLink}`
      const waLink = `https://wa.me/?text=${encodeURIComponent(msg)}`
      window.open(waLink, '_blank')
    }
  }

  return (
    <div>
      <PageHeader
        title="Team Management"
        action={{
          label: 'Invite Agent',
          icon: <UserPlus className="w-4 h-4" />,
          onClick: () => {
            trackButtonClick('Open Invite Modal', { page: 'team' })
            setShowInviteModal(true)
          },
        }}
      />

      {/* Agents Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Active Agents</h2>
        </div>
        {profilesLoading ? (
          <div className="p-6 text-center">
            <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-saffron rounded-full animate-spin" />
          </div>
        ) : profiles.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                    Last Login
                  </th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody>
                {profiles.map((profile) => (
                  <tr key={profile.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {getInitials(profile.full_name)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{profile.full_name}</p>
                          <p className="text-xs text-gray-600">{profile.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          profile.role === 'admin'
                            ? 'bg-navy text-white'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {profile.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          profile.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {profile.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {profile.last_login_at ? timeAgo(profile.last_login_at) : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {profile.id !== user?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            toggleStatusMutation.mutate({
                              id: profile.id,
                              is_active: !profile.is_active,
                            })
                          }
                          isLoading={toggleStatusMutation.isPending}
                        >
                          {profile.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-600">You are the only agent in this agency.</div>
        )}
      </div>

      {/* Pending Invitations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Pending Invitations</h2>
        </div>
        {invitationsLoading ? (
          <div className="p-6 text-center">
            <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-saffron rounded-full animate-spin" />
          </div>
        ) : invitations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                    Mobile No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                    Invited
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                    Status
                  </th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody>
                {invitations.map((invitation) => (
                  <tr key={invitation.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invitation.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {invitation.phone || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDate(invitation.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDate(invitation.expires_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          invitation.status === 'pending'
                            ? 'bg-amber-100 text-amber-800'
                            : invitation.status === 'accepted'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {invitation.status === 'accepted' && (
                          <CheckCircle className="w-3 h-3 mr-1" />
                        )}
                        {invitation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                      {invitation.status === 'pending' && invitation.invite_link && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              trackButtonClick('Copy Invite Link', { invitationId: invitation.id })
                              navigator.clipboard.writeText(invitation.invite_link!)
                              toast.success('Link copied!')
                            }}
                            title="Copy invite link"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              trackButtonClick('Delete Invitation', { invitationId: invitation.id, email: invitation.email })
                              setConfirmDelete(invitation.id)
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-600">No pending invitations</div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {inviteLink ? 'Share Invite Link' : 'Invite New Agent'}
            </h2>

            {!inviteLink ? (
              <form onSubmit={handleInviteSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="agent@example.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                    disabled={inviteMutation.isPending}
                  />
                  {inviteEmail && !validateEmail(inviteEmail) && (
                    <p className="mt-1 text-xs text-red-600">Invalid email address.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile No (optional)
                  </label>
                  <input
                    type="tel"
                    value={invitePhone}
                    onChange={(e) => setInvitePhone(e.target.value)}
                    placeholder="+91 98765 43210 or 9876543210"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                    disabled={inviteMutation.isPending}
                  />
                  {invitePhone && !validatePhone(invitePhone) && (
                    <p className="mt-1 text-xs text-red-600">Invalid phone number. Use 10 digits.</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => {
                      trackButtonClick('Cancel Invite', { page: 'team' })
                      setShowInviteModal(false)
                    }}
                    disabled={inviteMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    isLoading={inviteMutation.isPending}
                  >
                    Send Invite
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invite Link
                  </label>
                  <input
                    type="text"
                    value={inviteLink}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onClick={handleCopyLink}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleShareWhatsApp}
                  >
                    Share on WhatsApp
                  </Button>
                </div>

                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    trackButtonClick('Close Invite Modal', { page: 'team' })
                    setShowInviteModal(false)
                    setInviteLink(null)
                    setInviteEmail('')
                    setInvitePhone('')
                  }}
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) {
            revokeMutation.mutate(confirmDelete)
          }
        }}
        title="Revoke Invitation"
        description="Are you sure you want to revoke this invitation? The recipient won't be able to accept it."
        confirmLabel="Revoke"
        confirmVariant="destructive"
        isLoading={revokeMutation.isPending}
      />
    </div>
  )
}
